import { FC, useCallback } from 'react';

import cn from 'classnames';
import { uniqBy } from 'lodash';
import { useDropzone } from 'react-dropzone';
import { BsPlusLg } from 'react-icons/bs';
import { convertToMegabytes, downloadURI } from 'utils/helpers';
import AttachmentsTooLargeModal from 'modules/account/contact/components/AttachmentsTooLargeModal';
import { Button, DeleteItemModal } from 'ui/components/common';
import UploadItemsList from 'ui/components/common/Entry/NotesEntry/UploadItemsList';
import UploadListItem from 'ui/partials/Composer/UploadListItem';
import { useDeleteAttachment } from 'modules/account/contact/hooks';
import { useGetSubscription } from 'modules/account/settings/hooks';
import useModal from 'ui/partials/Modal/useModal';
// import { SupportedFileInput } from '@uploadcare/upload-client';
import { SIZE } from 'utils/constants';
import { Attachment } from 'utils/types';

const maxSize = 1024 * 1024 * 5;

type PROPS = {
  isEditing?: boolean;
  isLoading?: boolean;
  attachments: Attachment[];
  addAttachmentToUpload: React.Dispatch<React.SetStateAction<File[]>>;
  attachmentsToUpload: File[];
  taskId: string;
};

const TaskAttachments: FC<PROPS> = ({
  isEditing = false,
  attachments = [],
  attachmentsToUpload = [],
  isLoading = false,
  addAttachmentToUpload,
  taskId,
}) => {
  const { showModal } = useModal();
  const { data: subscription } = useGetSubscription();
  const { mutate: deleteAttachment, isLoading: deleteLoading } =
    useDeleteAttachment();

  const showAttachmentsTooLargeModal = (fileSize: string) => {
    showModal({
      title: 'Attachments',
      disableOutsideClick: true,
      content: (
        <AttachmentsTooLargeModal fileSize={fileSize} maxSize={maxSize} />
      ),
    });
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    maxSize,
    noClick: true,
    noKeyboard: true,
    onDrop: (acceptedFiles) => {
      addAttachmentToUpload(
        uniqBy([...attachmentsToUpload, ...acceptedFiles], 'name'),
      );

      const sizeToUpload = attachments.reduce(
        (acc, curr) => acc + (curr?.size ?? 0),
        0,
      );

      if (sizeToUpload > maxSize) {
        showAttachmentsTooLargeModal(convertToMegabytes(sizeToUpload));
      }
    },
    onDropRejected: (fileRejections) => {
      const totalSize = fileRejections.reduce(
        (acc, { errors: fileErrors, file }) => {
          if (fileErrors[0].code === 'file-too-large') {
            return acc + file.size;
          }
          return acc;
        },
        0,
      );

      if (fileRejections[0].errors[0].code === 'file-too-large') {
        showAttachmentsTooLargeModal(
          `file ${convertToMegabytes(totalSize)} to large.`,
        );
      }
    },
  });

  const handleDeleteAttachment = useCallback(
    (attachmentId: string) => {
      deleteAttachment({
        id: attachmentId,
        referenceId: taskId,
      });
    },
    [deleteAttachment, taskId],
  );

  const showAttachmentDeleteModal = useCallback(
    (attachmentId: string) => {
      showModal({
        title: 'Attachment',
        content: (
          <DeleteItemModal
            text="attachment"
            onClickDelete={() => handleDeleteAttachment(attachmentId)}
            isLoading={deleteLoading}
            validation
          />
        ),
        disableOutsideClick: true,
      });
    },
    [deleteLoading, handleDeleteAttachment, showModal],
  );

  const handleRemoveAttachment = (fileName: string) => {
    const newAttachments = attachmentsToUpload.filter(
      (attachment) => (attachment as unknown as File).name !== fileName,
    );

    addAttachmentToUpload(newAttachments);
  };

  return (
    <>
      <div {...getRootProps()}>
        {!subscription?.isExpired && (
          <Button
            intent={'tertiary'}
            size={'sm'}
            label="ADD FILE"
            onClick={open}
            icon={BsPlusLg}
            left
            disabled={isLoading}
          />
        )}
        <input {...getInputProps()} />
        {isDragActive && (
          <div
            className={cn(
              'absolute inset-y-0 left-0',
              'bg-white',
              'bg-opacity-90',
              'h-full w-full',
              'grid place-items-center',
              'border-2 border-dashed border-secondary',
            )}
          >
            <div className="text-lg">Drop Files Here</div>
          </div>
        )}
      </div>
      <div className="max-h-[164px] space-y-1 overflow-y-auto">
        {attachmentsToUpload.map((file) => (
          <UploadListItem
            key={file.name}
            showSize
            file={{
              ...file,
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
            }}
            size={SIZE.Large}
            onRemove={() => handleRemoveAttachment(file.name)}
            isUploaded={false}
          />
        ))}
      </div>
      <UploadItemsList
        isEditing={isEditing}
        attachments={attachments}
        showRemove
        onDownload={downloadURI}
        onRemove={showAttachmentDeleteModal}
      />
    </>
  );
};

export default TaskAttachments;
